import { useState } from 'react';
import {
  App,
  Button,
  Card,
  Dropdown,
  Flex,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  type MenuProps,
} from 'antd';
import { useAdminApi } from '../admin-api-context.js';
import {
  JsonMutationDrawer,
  type MutationTemplate,
} from '../components/json-mutation-drawer.js';
import { PageShell, RefreshButton } from '../components/page-shell.js';
import type {
  Configuration,
  OperationProfileSummary,
  ProviderAccountSummary,
} from '../types.js';
import { useAdminQuery } from '../use-admin-query.js';

const UUID = '00000000-0000-4000-8000-000000000000';

export function ConfigurationPage() {
  const query = useAdminQuery<Configuration>('/admin/v1/configuration');
  const [template, setTemplate] = useState<MutationTemplate>();
  const actions: MenuProps['items'] = mutationTemplates.map((item, index) => ({
    key: String(index),
    label: item.label,
    onClick: () => setTemplate(item),
  }));

  return (
    <PageShell
      title="Platform configuration"
      description="Provider credentials are product- and environment-scoped. Routing controls model selection; products cannot override it."
      action={
        <Space>
          <RefreshButton
            loading={query.loading}
            onClick={() => void query.refresh()}
          />
          <Dropdown menu={{ items: actions }} trigger={['click']}>
            <Button type="primary">Add configuration</Button>
          </Dropdown>
        </Space>
      }
      error={query.error}
    >
      <Card loading={query.loading}>
        <Tabs
          items={configurationTabs(query.data, () => void query.refresh())}
        />
      </Card>
      <JsonMutationDrawer
        template={template}
        onClose={() => setTemplate(undefined)}
        onComplete={() => {
          setTemplate(undefined);
          void query.refresh();
        }}
      />
    </PageShell>
  );
}

function configurationTabs(
  configuration: Configuration | undefined,
  refresh: () => void,
) {
  return [
    {
      key: 'products',
      label: 'Products',
      children: (
        <Table
          rowKey="id"
          pagination={false}
          dataSource={configuration?.products ?? []}
          columns={[
            { title: 'Product', dataIndex: 'label' },
            { title: 'Key', dataIndex: 'key' },
            {
              title: 'ID',
              dataIndex: 'id',
              render: (id: string) => <CopyableId value={id} />,
            },
            {
              title: 'Retention',
              render: (_, row) => `${row.contentRetentionSeconds / 60} min`,
            },
            {
              title: 'Live retention',
              render: (_, row) => `${row.liveContentRetentionSeconds / 60} min`,
            },
            {
              title: 'Signing keys',
              render: (_, row) =>
                row.signingKeys.map((key) => (
                  <Tag
                    key={key.id}
                    color={key.status === 'ACTIVE' ? 'green' : 'blue'}
                  >
                    {key.keyId}: {key.status}
                  </Tag>
                )),
            },
            {
              title: 'Upload hosts',
              render: (_, row) => row.allowlistedUploadHosts.join(', ') || '—',
            },
          ]}
        />
      ),
    },
    {
      key: 'providers',
      label: 'Providers and models',
      children: (
        <Flex vertical gap={16}>
          {(configuration?.providerAccounts ?? []).map((account) => (
            <ProviderCard key={account.id} account={account} />
          ))}
        </Flex>
      ),
    },
    {
      key: 'operations',
      label: 'Operations and rollout',
      children: (
        <OperationsTable
          operations={configuration?.operationProfiles ?? []}
          products={configuration?.products ?? []}
          onUpdated={refresh}
        />
      ),
    },
    {
      key: 'routing',
      label: 'Routing',
      children: <JsonTable rows={configuration?.routingPolicies ?? []} />,
    },
    {
      key: 'limits',
      label: 'Capacity limits',
      children: <JsonTable rows={configuration?.limits ?? []} />,
    },
    {
      key: 'budgets',
      label: 'Budgets',
      children: <JsonTable rows={configuration?.budgets ?? []} />,
    },
  ];
}

function ProviderCard({ account }: { account: ProviderAccountSummary }) {
  return (
    <Card
      size="small"
      title={`${account.provider} · ${account.label}`}
      extra={
        <Tag color={account.isActive ? 'green' : 'default'}>
          {account.isActive ? 'Active' : 'Inactive'}
        </Tag>
      }
    >
      <Typography.Paragraph type="secondary">
        Account ID: <CopyableId value={account.id} />
      </Typography.Paragraph>
      <Typography.Title level={5}>Credentials</Typography.Title>
      <Table
        rowKey="id"
        size="small"
        pagination={false}
        dataSource={account.credentials}
        columns={[
          { title: 'Alias', dataIndex: 'alias' },
          { title: 'Project', dataIndex: 'projectKey' },
          { title: 'Envelope key', dataIndex: 'secretKeyVersion' },
          { title: 'Health', dataIndex: 'lastHealthStatus', render: dash },
          { title: 'Active', dataIndex: 'isActive', render: yesNo },
        ]}
      />
      <Typography.Title level={5} className="subsection-title">
        Models
      </Typography.Title>
      <Table
        rowKey="id"
        size="small"
        pagination={false}
        dataSource={account.models}
        columns={[
          { title: 'Model', dataIndex: 'providerModelId' },
          {
            title: 'ID',
            dataIndex: 'id',
            render: (id: string) => <CopyableId value={id} />,
          },
          { title: 'Family', dataIndex: 'family' },
          { title: 'Quality', dataIndex: 'qualityTier' },
          {
            title: 'Capabilities',
            dataIndex: 'capabilities',
            render: (values: string[]) =>
              values.map((value) => <Tag key={value}>{value}</Tag>),
          },
          {
            title: 'Prices',
            dataIndex: 'prices',
            render: (prices: Array<{ id: string; version: string }>) =>
              prices.map((price) => <Tag key={price.id}>{price.version}</Tag>),
          },
        ]}
      />
    </Card>
  );
}

function OperationsTable({
  operations,
  products,
  onUpdated,
}: {
  operations: OperationProfileSummary[];
  products: Configuration['products'];
  onUpdated: () => void;
}) {
  const api = useAdminApi();
  const { message } = App.useApp();
  async function rollout(
    operation: OperationProfileSummary,
    executionMode: OperationProfileSummary['executionMode'],
    canaryPercentage: 0 | 5 | 25 | 100,
  ): Promise<void> {
    try {
      await api.send('PATCH', `/admin/v1/operations/${operation.id}/rollout`, {
        executionMode,
        canaryPercentage,
      });
      await message.success(`${operation.key} rollout updated`);
      onUpdated();
    } catch (error) {
      await message.error(
        error instanceof Error ? error.message : 'Rollout update failed',
      );
    }
  }
  return (
    <Table
      rowKey="id"
      pagination={{ pageSize: 25 }}
      dataSource={operations}
      columns={[
        {
          title: 'Product',
          render: (_, row) =>
            products.find((product) => product.id === row.productId)?.key ??
            row.productId,
        },
        { title: 'Operation', dataIndex: 'key' },
        {
          title: 'ID',
          dataIndex: 'id',
          render: (id: string) => <CopyableId value={id} />,
        },
        { title: 'Version', dataIndex: 'version' },
        { title: 'Mode', dataIndex: 'executionMode' },
        { title: 'Canary', render: (_, row) => `${row.canaryPercentage}%` },
        {
          title: 'Controlled rollout',
          render: (_, row) => (
            <Space wrap>
              <Button
                size="small"
                onClick={() => void rollout(row, 'LOCAL', 0)}
              >
                Local
              </Button>
              <Button
                size="small"
                onClick={() => void rollout(row, 'DRY_RUN', 100)}
              >
                Dry run / 100%
              </Button>
              <Button
                size="small"
                onClick={() => void rollout(row, 'PLATFORM', 100)}
              >
                Platform / 100%
              </Button>
            </Space>
          ),
        },
      ]}
    />
  );
}

function JsonTable({
  rows,
}: {
  rows: Array<Record<string, unknown> & { id: string }>;
}) {
  return (
    <Table
      rowKey="id"
      pagination={{ pageSize: 20 }}
      dataSource={rows}
      columns={[
        {
          title: 'ID',
          dataIndex: 'id',
          render: (id: string) => <CopyableId value={id} />,
        },
        {
          title: 'Key',
          render: (_, row) => displayKey(row),
        },
        {
          title: 'Configuration',
          render: (_, row) => (
            <code className="compact-json">{JSON.stringify(row)}</code>
          ),
        },
      ]}
    />
  );
}

function yesNo(value: unknown): string {
  return value ? 'Yes' : 'No';
}

function dash(value: unknown): string {
  return typeof value === 'string' && value ? value : '—';
}

function CopyableId({ value }: { value: string }) {
  return (
    <Typography.Text code copyable={{ text: value }}>
      {value}
    </Typography.Text>
  );
}

function displayKey(row: Record<string, unknown> & { id: string }): string {
  const candidate = row['scopeKey'] ?? row['key'];
  return typeof candidate === 'string' ? candidate : row.id;
}

const mutationTemplates: MutationTemplate[] = [
  {
    label: 'Provider account',
    path: '/admin/v1/provider-accounts',
    body: {
      provider: 'OPENAI',
      key: 'openai-main',
      label: 'OpenAI',
      isActive: true,
    },
  },
  {
    label: 'Encrypted product credential',
    path: '/admin/v1/credentials',
    sensitive: true,
    body: {
      providerAccountId: UUID,
      productId: UUID,
      projectKey: 'product-project',
      alias: 'primary',
      secret: 'replace-with-provider-secret',
      isActive: true,
    },
  },
  {
    label: 'Model',
    path: '/admin/v1/models',
    body: {
      providerAccountId: UUID,
      providerModelId: 'provider-model-id',
      family: 'model-family',
      label: 'Model label',
      capabilities: ['text'],
      privacyTags: ['no-training'],
      allowedRegions: [],
      qualityTier: 'standard',
      contextWindow: 128000,
      maximumOutputTokens: 16384,
      supportsStoreDisabled: true,
    },
  },
  {
    label: 'Effective-dated price',
    path: '/admin/v1/prices',
    body: {
      modelId: UUID,
      version: '2026-08',
      inputMicrousdPerMillion: '0',
      outputMicrousdPerMillion: '0',
      cachedInputMicrousdPerMillion: '0',
      imageInputMicrousdPerMillion: '0',
      imageOutputMicrousdPerMillion: '0',
      audioInputMicrousdPerMillionMs: '0',
      audioOutputMicrousdPerMillionMs: '0',
      effectiveFrom: new Date().toISOString(),
    },
  },
  {
    label: 'Routing policy',
    path: '/admin/v1/routing-policies',
    body: {
      operationProfileId: UUID,
      key: 'default',
      maximumAttempts: 4,
      maximumAttemptsPerTarget: 2,
      maximumFailovers: 2,
      maximumDurationMs: 600000,
      attemptTimeoutMs: 120000,
      allowQualityDowngrade: false,
    },
  },
  {
    label: 'Routing target',
    path: '/admin/v1/routing-targets',
    body: {
      routingPolicyId: UUID,
      modelId: UUID,
      credentialId: UUID,
      position: 0,
      weight: 100,
      allowedPlans: [],
    },
  },
  {
    label: 'Capacity limit',
    path: '/admin/v1/capacity-limits',
    body: {
      scopeType: 'PRODUCT',
      scopeKey: 'product:schooltin',
      productId: UUID,
      requestsPerMinute: 60,
      maximumConcurrency: 10,
      reservedConcurrency: 2,
      resetTimezone: 'UTC',
      cooldownSeconds: 60,
    },
  },
  {
    label: 'Budget',
    path: '/admin/v1/budgets',
    body: {
      productId: UUID,
      scopeKey: 'product:schooltin',
      period: 'monthly',
      amountMicrousd: '100000000',
      warningPercent: 80,
      hardCapEnabled: false,
      effectiveFrom: new Date().toISOString(),
    },
  },
  {
    label: 'Service signing public key',
    path: '/admin/v1/signing-keys',
    body: {
      productId: UUID,
      keyId: 'product-staging-next',
      publicKeyPem:
        '-----BEGIN PUBLIC KEY-----\nreplace-me\n-----END PUBLIC KEY-----',
      status: 'NEXT',
    },
  },
];
